import { type App, reactive, type Plugin, type ComponentCustomProperties, onUnmounted } from 'vue'
import SimpleLoading from './SimpleLoading.vue'

// store
export interface LoadingStore {
  show: boolean
  nowLoading: boolean
}
const loadingStore = reactive<LoadingStore>({
  show: false,
  nowLoading: false,
})

let timeout: ReturnType<typeof setTimeout> | null = null
// 진행 중인 show() 횟수. 여러 요청이 동시에 로딩을 띄워도 마지막 요청이 끝날 때 닫는다.
let activeCount = 0

// loading object
interface Loading {
  show: () => void
  hide: () => void
  reset: () => void
  nowLoading: () => boolean
}

function close() {
  loadingStore.nowLoading = false
  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }
  loadingStore.show = false
}

export const $loading: Loading = {
  show: () => {
    activeCount++
    if (activeCount > 1) return
    loadingStore.nowLoading = true
    if (timeout) clearTimeout(timeout)

    timeout = setTimeout(() => {
      loadingStore.show = true
    }, 150)
  },
  hide: () => {
    if (activeCount === 0) return
    activeCount--
    if (activeCount === 0) close()
  },
  // 횟수와 관계없이 닫는다 (로그인 화면 이동 등)
  reset: () => {
    activeCount = 0
    close()
  },
  nowLoading: () => loadingStore.nowLoading,
}

// 화면 진입 조회용. track()으로 감싼 작업이 끝날 때까지 로딩을 띄우고,
// 끝나기 전에 화면이 사라지면(다른 화면으로 이동) 남은 로딩을 닫아 다음 화면에 남지 않게 한다.
export function usePageLoading() {
  let pending = 0
  onUnmounted(() => {
    for (; pending > 0; pending--) $loading.hide()
  })

  async function track<T>(task: Promise<T> | (() => Promise<T>)): Promise<T> {
    pending++
    $loading.show()
    try {
      return await (typeof task === 'function' ? task() : task)
    } finally {
      if (pending > 0) {
        pending--
        $loading.hide()
      }
    }
  }

  return { track }
}

const SimpleLoadingPlugin: Plugin = {
  install(app: App) {
    app.config.globalProperties.$loading = $loading

    app.provide('loading', $loading)
    app.provide('loadingStore', loadingStore)

    app.component('SimpleLoading', SimpleLoading)
  },
}

export default SimpleLoadingPlugin as Plugin

// --------------------------------------------------------------
// intellisense 를 위한 설정
// --------------------------------------------------------------
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $loading: Loading
  }
}

// Composition API - provide/inject를 위한 전역 키 정의
declare module 'vue' {
  export function inject(key: 'loading'): Loading
  export function inject(key: 'loadingStore'): LoadingStore
}
